/**
 * Google Consent Mode: wrap gtag/dataLayer, update GCM, retry.
 * @package AdvancedTrackingManager
 */

import type { AtmConsentConfig } from '@lib/types';
import { createLogger } from '@lib/logger';

const w = globalThis as unknown as Window;

const GCM_KEYS_NON_NECESSARY = [
  'ad_storage',
  'ad_user_data',
  'ad_personalization',
  'analytics_storage',
  'personalization_storage',
];

export const forceGcmPayloadDeniedUnlessNecessary = (
  payload: Record<string, string>,
  config: AtmConsentConfig
): void => {
  if (
    !config.turnstileConfigured ||
    w._atmTurnstileFailed !== true ||
    typeof payload !== 'object'
  ) {
    return;
  }
  let changed = false;
  for (const key of GCM_KEYS_NON_NECESSARY) {
    if (payload[key] === 'granted') {
      payload[key] = 'denied';
      changed = true;
    }
  }
  if (changed) {
    createLogger('ATM Consent Handler', config.debugMode).debug(
      '🚫 gtag consent update blocked granted (Turnstile failed) - forced non-necessary to denied'
    );
  }
};

export const applyDebugModeToConfig = (
  options: Record<string, unknown>,
  shouldAddGtagDebugMode: boolean
): void => {
  if (!shouldAddGtagDebugMode || typeof options !== 'object') return;
  options.debug_mode = true;
};

export const patchDataLayerForDebugMode = (
  shouldAddGtagDebugMode: boolean,
  config: AtmConsentConfig
): void => {
  if (!shouldAddGtagDebugMode || typeof w.dataLayer === 'undefined') return;
  if (w._atmDebugModePatched) return;
  w._atmDebugModePatched = true;
  for (let i = 0; i < w.dataLayer!.length; i += 1) {
    const item = w.dataLayer![i] as unknown[] | undefined;
    if (item && item[0] === 'config' && item.length >= 2) {
      if (!item[2]) item[2] = {};
      (item[2] as Record<string, unknown>).debug_mode = true;
    }
  }
  createLogger('ATM Consent Handler', config.debugMode).debug(
    'GA4 debug_mode enabled for DebugView'
  );
};

export const ensureGtagWrapped = (
  config: AtmConsentConfig,
  shouldAddGtagDebugMode: boolean
): void => {
  if (w._atmGtagWrapped) return;
  if (typeof w.gtag !== 'function') return;
  w._atmGtagWrapped = true;
  const originalGtag = w.gtag!;
  w.gtag = ((...args: unknown[]) => {
    if (
      args.length >= 3 &&
      args[0] === 'consent' &&
      args[1] === 'update' &&
      typeof args[2] === 'object' &&
      args[2] !== null
    ) {
      forceGcmPayloadDeniedUnlessNecessary(args[2] as Record<string, string>, config);
    }
    if (
      shouldAddGtagDebugMode &&
      args.length >= 2 &&
      args[0] === 'config' &&
      typeof args[2] === 'object' &&
      args[2] !== null
    ) {
      applyDebugModeToConfig(args[2] as Record<string, unknown>, shouldAddGtagDebugMode);
    }
    if (shouldAddGtagDebugMode && args.length === 2 && args[0] === 'config') {
      args[2] = { debug_mode: true };
    }
    return originalGtag.apply(w, args as Parameters<typeof originalGtag>);
  }) as typeof originalGtag;
};

export const ensureDataLayerPushWrapped = (
  config: AtmConsentConfig,
  shouldAddGtagDebugMode: boolean
): void => {
  if (w._atmDataLayerPushWrapped) return;
  if (typeof w.dataLayer === 'undefined') return;
  const origPush = w.dataLayer!.push.bind(w.dataLayer);
  w.dataLayer!.push = ((...items: unknown[]) => {
    for (const item of items) {
      if (
        item !== null &&
        item !== undefined &&
        typeof (item as { length?: number }).length === 'number' &&
        (item as unknown[]).length >= 3 &&
        (item as unknown[])[0] === 'consent' &&
        (item as unknown[])[1] === 'update' &&
        typeof (item as unknown[])[2] === 'object'
      ) {
        forceGcmPayloadDeniedUnlessNecessary((item as unknown[])[2] as Record<string, string>, config);
      }
      if (
        shouldAddGtagDebugMode &&
        item &&
        (item as unknown[])[0] === 'config' &&
        (item as unknown[]).length >= 2
      ) {
        const arr = item as unknown[];
        if (!arr[2]) arr[2] = {};
        applyDebugModeToConfig(arr[2] as Record<string, unknown>, shouldAddGtagDebugMode);
      }
    }
    return origPush(...items);
  }) as typeof Array.prototype.push;
};

export const ensureGtagAndDataLayerWrapped = (
  config: AtmConsentConfig,
  shouldAddGtagDebugMode: boolean
): void => {
  ensureGtagWrapped(config, shouldAddGtagDebugMode);
  ensureDataLayerPushWrapped(config, shouldAddGtagDebugMode);
  patchDataLayerForDebugMode(shouldAddGtagDebugMode, config);
};

export const updateGoogleConsentMode = (
  updates: Record<string, string>,
  config: AtmConsentConfig
): void => {
  const log = createLogger('ATM Consent Handler', config.debugMode);
  let updated = false;

  if (typeof w.gtag === 'function') {
    try {
      w.gtag('consent', 'update', updates);
      updated = true;
    } catch (error) {
      log.debug('⚠️ Error updating Google Consent Mode via gtag()', error);
    }
  }

  if (typeof w.dataLayer !== 'undefined') {
    try {
      w.dataLayer.push({ event: 'consent_update', consent: updates });
      w.dataLayer.push(['consent', 'update', updates]);
      updated = true;
    } catch (error) {
      log.debug('⚠️ Error pushing to dataLayer', error);
    }
  }

  if (!updated) {
    log.debug('⚠️ Google Consent Mode not available - will retry');
    if (!w._atmPendingGcmUpdates) {
      w._atmPendingGcmUpdates = [];
    }
    w._atmPendingGcmUpdates.push(updates);
    scheduleGcmRetry(config);
  }
};

export const scheduleGcmRetry = (config: AtmConsentConfig): void => {
  if (w._atmGcmRetryInterval) return;

  const log = createLogger('ATM Consent Handler', config.debugMode);

  w._atmGcmRetryInterval = setInterval(() => {
    if (!w._atmPendingGcmUpdates || w._atmPendingGcmUpdates.length === 0) {
      if (w._atmGcmRetryInterval) {
        clearInterval(w._atmGcmRetryInterval);
        w._atmGcmRetryInterval = null;
      }
      return;
    }

    const updates = w._atmPendingGcmUpdates.shift()!;
    let applied = false;

    if (typeof w.gtag === 'function') {
      try {
        w.gtag('consent', 'update', updates);
        log.debug('✅ Applied queued Google Consent Mode update', updates);
        applied = true;
      } catch (error) {
        log.debug('⚠️ Error applying queued GCM update', error);
      }
    }

    if (!applied && typeof w.dataLayer !== 'undefined') {
      try {
        w.dataLayer.push(['consent', 'update', updates]);
        log.debug('✅ Applied queued Google Consent Mode update via dataLayer', updates);
        applied = true;
      } catch (error) {
        log.debug('⚠️ Error applying queued GCM update via dataLayer', error);
      }
    }

    if (!applied) {
      w._atmPendingGcmUpdates.unshift(updates);
    }

    if (w._atmGcmRetryCount === undefined) w._atmGcmRetryCount = 0;
    w._atmGcmRetryCount += 1;
    if (w._atmGcmRetryCount > 50) {
      log.debug('⚠️ GCM retry timeout - giving up');
      if (w._atmGcmRetryInterval) {
        clearInterval(w._atmGcmRetryInterval);
        w._atmGcmRetryInterval = null;
      }
      w._atmPendingGcmUpdates = [];
      w._atmGcmRetryCount = 0;
    }
  }, 100);
};
